import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  FolderInput,
  ChevronRight,
  GripVertical,
  Minus,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import {
  COLUMN_LABEL_MAX_LENGTH,
  clearDetailColumns,
  resultColumnKeys,
  setColumnLabel,
  toggleDetailColumn,
} from '../../reporting/definitionUpdates';
import { effectiveDetailColumns } from '../../reporting/detailColumns';
import {
  addDisplayColumn,
  effectiveDisplayColumns,
  moveDisplayColumn,
  moveDisplayColumnField,
  moveDisplayColumnFieldToBottom,
  removeDisplayColumn,
  reorderDisplayColumnFields,
  updateDisplayColumnLabel,
} from '../../reporting/displayColumns';
import {
  NO_RELATED,
  refLabel,
  type RelatedManifests,
} from '../../reporting/columnRefs';
import { PATIENT_ROSTER_OUTPUT_COLUMNS } from '../../reporting/patientRosterColumns';
import {
  columnLabelRefs,
  defaultColumnLabels,
} from '../../reporting/columnNames';
import { humanizeHeader } from '../../reporting/formatValue';
import type {
  DatasetManifestEntry,
  DisplayColumn,
  ReportDefinition,
} from '../../types/reporting';

interface TableColumnsEditorProps {
  definition: ReportDefinition;
  dataset?: DatasetManifestEntry;
  related?: RelatedManifests;
  onChange: (definition: ReportDefinition) => void;
}

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <label className='text-xs font-semibold uppercase text-muted-foreground'>
    {children}
  </label>
);

type DraftDisplayGroup = {
  id: string;
  label: string;
  fields: string[];
};

const LabelInput = ({
  definition,
  field,
  defaultName,
  onChange,
}: {
  definition: ReportDefinition;
  field: string;
  defaultName: string;
  onChange: (definition: ReportDefinition) => void;
}) => {
  const custom = definition.column_labels?.[field];
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? custom ?? defaultName;
  return (
    <>
      <Input
        inputSize='sm'
        className='min-w-0 flex-1'
        aria-label={`Name for ${defaultName}`}
        maxLength={COLUMN_LABEL_MAX_LENGTH}
        value={shown}
        onFocus={event => {
          setDraft(shown);
          event.target.select();
        }}
        onBlur={() => setDraft(null)}
        onChange={event => {
          setDraft(event.target.value);
          onChange(
            setColumnLabel(definition, field, event.target.value, defaultName)
          );
        }}
      />
      {custom !== undefined && (
        <Button
          size='sm'
          variant='ghost'
          aria-label={`Reset ${defaultName}`}
          onClick={() => {
            setDraft(null);
            onChange(setColumnLabel(definition, field, '', defaultName));
          }}
        >
          <RotateCcw className='h-4 w-4' />
        </Button>
      )}
      <span className='sr-only'>{shown}</span>
    </>
  );
};

const GroupLabelInput = ({
  definition,
  displayIndex,
  updateIndex,
  label,
  onChange,
}: {
  definition: ReportDefinition;
  displayIndex: number;
  updateIndex: number;
  label: string;
  onChange: (definition: ReportDefinition) => void;
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <Input
      inputSize='sm'
      className='min-w-0 flex-1'
      aria-label={`Group ${displayIndex + 1} label`}
      value={draft ?? label}
      onFocus={() => setDraft(label)}
      onBlur={() => setDraft(null)}
      onChange={event => {
        const value = event.target.value;
        setDraft(value);
        if (value.trim())
          onChange(updateDisplayColumnLabel(definition, updateIndex, value));
      }}
    />
  );
};

interface SortableDisplayFieldProps {
  id: string;
  field: string;
  label: string;
  defaultName: string;
  draftId?: string;
  sourceIndex?: number;
  index: number;
  count: number;
  onToggle: () => void;
  onMove: (from: number, to: number) => void;
  onRemoveFromGroup?: () => void;
  definition: ReportDefinition;
  onChange: (definition: ReportDefinition) => void;
}

const SortableDisplayField = ({
  id,
  field,
  label,
  defaultName,
  draftId,
  sourceIndex,
  index,
  count,
  onToggle,
  onMove,
  onRemoveFromGroup,
  definition,
  onChange,
}: SortableDisplayFieldProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: { kind: 'child', draftId, sourceIndex },
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex min-w-0 items-center gap-1 rounded border bg-background px-1 py-0.5 text-sm ${isDragging ? 'z-10 opacity-60' : ''}`.trim()}
      data-field={field}
    >
      <button
        type='button'
        className='cursor-grab touch-none p-1 text-muted-foreground active:cursor-grabbing'
        aria-label={`Drag ${label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className='h-4 w-4' />
      </button>
      <Checkbox
        checkboxSize='sm'
        checked
        aria-label={label}
        id={`col-${field}`}
        onCheckedChange={onToggle}
      />
      <LabelInput
        definition={definition}
        field={field}
        defaultName={defaultName}
        onChange={onChange}
      />
      {onRemoveFromGroup && (
        <Button
          size='sm'
          variant='ghost'
          aria-label={`Remove ${label} from group`}
          title={`Remove ${label} from group`}
          onClick={onRemoveFromGroup}
        >
          <Minus className='h-4 w-4' />
        </Button>
      )}
      <Button
        size='lg'
        variant='ghost'
        className='md:hidden'
        aria-label={`Move ${label} up`}
        disabled={index === 0}
        onClick={() => onMove(index, index - 1)}
      >
        <ArrowUp className='h-4 w-4' />
      </Button>
      <Button
        size='lg'
        variant='ghost'
        className='md:hidden'
        aria-label={`Move ${label} down`}
        disabled={index === count - 1}
        onClick={() => onMove(index, index + 1)}
      >
        <ArrowDown className='h-4 w-4' />
      </Button>
    </div>
  );
};

const AvailableDisplayField = ({
  field,
  label,
  onToggle,
}: {
  field: string;
  label: string;
  onToggle: () => void;
}) => (
  <div
    className='flex min-w-0 items-center gap-1 rounded border bg-background px-1 py-0.5 text-sm'
    data-available-field={field}
  >
    <Checkbox
      checkboxSize='sm'
      checked={false}
      aria-label={label}
      id={`col-${field}`}
      onCheckedChange={onToggle}
    />
    <span className='min-w-0 flex-1 break-words'>{label}</span>
  </div>
);

interface SortableDisplayEntryProps {
  id: string;
  column: DisplayColumn;
  index: number;
  sourceIndex: number;
  getFieldLabel: (field: string) => string;
  getColumnLabel: (field: string) => string;
  definition: ReportDefinition;
  onChange: (definition: ReportDefinition) => void;
  onToggleField: (field: string) => void;
  onMoveEntry: (from: number, to: number) => void;
  onMoveChild: (sourceIndex: number, from: number, to: number) => void;
  onRemoveChild: (sourceIndex: number, field: string) => void;
  onMoveToGroup: (
    sourceIndex: number,
    field: string,
    targetSourceIndex: number
  ) => void;
  groupTargets: { sourceIndex: number; label: string }[];
  onDelete: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  entryCount: number;
}

const SortableDisplayEntry = ({
  id,
  column,
  index,
  sourceIndex,
  getFieldLabel,
  getColumnLabel,
  definition,
  onChange,
  onToggleField,
  onMoveEntry,
  onMoveChild,
  onRemoveChild,
  onMoveToGroup,
  groupTargets,
  onDelete,
  collapsed,
  onToggleCollapsed,
  entryCount,
}: SortableDisplayEntryProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data: { kind: 'entry' } });
  const isGroup = column.fields.length > 1;
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`min-w-0 rounded border p-1 ${isDragging ? 'z-10 opacity-60' : ''}`.trim()}
      data-display-entry={index}
      data-testid={`display-entry-${id}`}
      data-display-id={id}
      data-display-fields={column.fields.join(',')}
    >
      <div className='flex min-w-0 flex-wrap items-center gap-1'>
        <button
          type='button'
          className='cursor-grab touch-none p-1 text-muted-foreground active:cursor-grabbing'
          aria-label={`Drag column ${column.label}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className='h-4 w-4' />
        </button>
        {isGroup ? (
          <GroupLabelInput
            definition={definition}
            displayIndex={index}
            updateIndex={sourceIndex}
            label={column.label}
            onChange={onChange}
          />
        ) : (
          <>
            <Checkbox
              checkboxSize='sm'
              checked
              aria-label={getFieldLabel(column.fields[0])}
              id={`col-${column.fields[0]}`}
              onCheckedChange={() => onToggleField(column.fields[0])}
            />
            <span className='min-w-0 flex-1 break-words'>
              {getColumnLabel(column.fields[0])}
            </span>
            {groupTargets.length > 0 && (
              <Select
                value=''
                onValueChange={value =>
                  onMoveToGroup(sourceIndex, column.fields[0], Number(value))
                }
              >
                <SelectTrigger
                  selectSize='sm'
                  className='w-auto'
                  aria-label={`Move ${getColumnLabel(column.fields[0])} to grouped column`}
                  title={`Move ${getColumnLabel(column.fields[0])} to grouped column`}
                >
                  <FolderInput className='h-4 w-4' />
                </SelectTrigger>
                <SelectContent>
                  {groupTargets.map(group => (
                    <SelectItem
                      key={group.sourceIndex}
                      value={String(group.sourceIndex)}
                    >
                      {group.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </>
        )}
        {isGroup && (
          <>
            <Button
              size='sm'
              variant='ghost'
              aria-label={`${collapsed ? 'Expand' : 'Collapse'} group ${column.label}`}
              aria-expanded={!collapsed}
              title={`${collapsed ? 'Expand' : 'Collapse'} group ${column.label}`}
              onClick={event => {
                event.stopPropagation();
                onToggleCollapsed();
              }}
            >
              {collapsed ? (
                <ChevronRight className='h-4 w-4' />
              ) : (
                <ChevronDown className='h-4 w-4' />
              )}
            </Button>
            <Button
              size='sm'
              variant='ghost'
              aria-label={`Delete group ${column.label}`}
              title={`Delete group ${column.label}`}
              onClick={onDelete}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </>
        )}
        {!isGroup && (
          <>
            <Button
              size='lg'
              variant='ghost'
              className='md:hidden'
              aria-label={`Move ${getFieldLabel(column.fields[0])} up`}
              disabled={index === 0}
              onClick={() => onMoveEntry(index, index - 1)}
            >
              <ArrowUp className='h-4 w-4' />
            </Button>
            <Button
              size='lg'
              variant='ghost'
              className='md:hidden'
              aria-label={`Move ${getFieldLabel(column.fields[0])} down`}
              disabled={index === entryCount - 1}
              onClick={() => onMoveEntry(index, index + 1)}
            >
              <ArrowDown className='h-4 w-4' />
            </Button>
          </>
        )}
      </div>
      {!isGroup &&
        definition.column_labels?.[column.fields[0]] !== undefined && (
          <p className='truncate px-1 text-xs text-muted-foreground'>
            Default: {getFieldLabel(column.fields[0])}
          </p>
        )}
      {isGroup && !collapsed && (
        <div
          className='mt-1 max-h-64 space-y-1 overflow-y-auto pl-6'
          data-display-field-list
        >
          <SortableContext
            items={column.fields.map(field => `display-child:${field}`)}
            strategy={verticalListSortingStrategy}
          >
            {column.fields.map((field, childIndex) => (
              <SortableDisplayField
                key={field}
                id={`display-child:${field}`}
                field={field}
                label={getColumnLabel(field)}
                defaultName={getFieldLabel(field)}
                sourceIndex={sourceIndex}
                index={childIndex}
                count={column.fields.length}
                onToggle={() => onToggleField(field)}
                onMove={(from, to) => onMoveChild(sourceIndex, from, to)}
                onRemoveFromGroup={() => onRemoveChild(sourceIndex, field)}
                definition={definition}
                onChange={onChange}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

const DraftGroup = ({
  group,
  collapsed,
  selectedDetailColumns,
  getFieldLabel,
  definition,
  onChange,
  onToggleCollapsed,
  onDelete,
  onLabelChange,
  onAddField,
  onRemoveField,
  onMoveField,
}: {
  group: DraftDisplayGroup;
  collapsed: boolean;
  selectedDetailColumns: string[];
  getFieldLabel: (field: string) => string;
  definition: ReportDefinition;
  onChange: (definition: ReportDefinition) => void;
  onToggleCollapsed: () => void;
  onDelete: () => void;
  onLabelChange: (label: string) => void;
  onAddField: (field: string) => void;
  onRemoveField: (field: string) => void;
  onMoveField: (from: number, to: number) => void;
}) => (
  <div
    className='min-w-0 rounded border border-dashed p-2'
    data-draft-group={group.id}
    data-testid={`draft-group-${group.id}`}
  >
    <div className='flex min-w-0 flex-wrap items-center gap-1'>
      <Input
        inputSize='sm'
        className='min-w-0 flex-1'
        aria-label={`Draft group ${group.id} label`}
        value={group.label}
        onChange={event => onLabelChange(event.target.value)}
      />
      {group.fields.length > 0 && (
        <Button
          size='sm'
          variant='ghost'
          aria-label={`${collapsed ? 'Expand' : 'Collapse'} draft group ${group.label}`}
          aria-expanded={!collapsed}
          title={`${collapsed ? 'Expand' : 'Collapse'} draft group ${group.label}`}
          onClick={onToggleCollapsed}
        >
          {collapsed ? (
            <ChevronRight className='h-4 w-4' />
          ) : (
            <ChevronDown className='h-4 w-4' />
          )}
        </Button>
      )}
      <Button
        size='sm'
        variant='ghost'
        aria-label={`Delete draft group ${group.label}`}
        onClick={onDelete}
      >
        <Trash2 className='h-4 w-4' />
      </Button>
    </div>
    {!collapsed && group.fields.length > 0 && (
      <SortableContext
        items={group.fields.map(field => `display-child:${field}`)}
        strategy={verticalListSortingStrategy}
      >
        <div
          className='mt-1 max-h-64 space-y-1 overflow-y-auto pl-1'
          data-display-field-list
        >
          {group.fields.map((field, index) => (
            <SortableDisplayField
              key={field}
              id={`display-child:${field}`}
              field={field}
              label={getFieldLabel(field)}
              defaultName={getFieldLabel(field)}
              draftId={group.id}
              index={index}
              count={group.fields.length}
              onToggle={() => onRemoveField(field)}
              onMove={onMoveField}
              definition={definition}
              onChange={onChange}
            />
          ))}
        </div>
      </SortableContext>
    )}
    <Select value='' onValueChange={onAddField}>
      <SelectTrigger selectSize='sm' className='mt-2'>
        <SelectValue placeholder='Add selected field' />
      </SelectTrigger>
      <SelectContent>
        {selectedDetailColumns
          .filter(field => !group.fields.includes(field))
          .map(field => (
            <SelectItem key={field} value={field}>
              {getFieldLabel(field)}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  </div>
);

const DetailColumnsEditor = ({
  dataset,
  definition,
  related,
  onChange,
}: {
  dataset: DatasetManifestEntry;
  definition: ReportDefinition;
  related: RelatedManifests;
  onChange: (definition: ReportDefinition) => void;
}) => {
  const roster = definition.pipeline?.type === 'patient_roster';
  const primarySelectableColumns = roster
    ? PATIENT_ROSTER_OUTPUT_COLUMNS
    : dataset.columns;
  const relatedSelectableColumns = roster
    ? []
    : [...related.entries()].flatMap(([key, entry]) =>
        entry.columns.map(column => ({
          name: `${key}.${column.name}`,
          label: column.label,
          type: column.type,
        }))
      );
  const selectableColumns = roster
    ? PATIENT_ROSTER_OUTPUT_COLUMNS
    : [...primarySelectableColumns, ...relatedSelectableColumns];
  const selectableFields = new Set(
    selectableColumns.map(column => column.name)
  );
  const selectedDetailColumns = effectiveDetailColumns(
    definition,
    dataset
  ).filter(field => selectableFields.has(field));
  const [draftGroups, setDraftGroups] = useState<DraftDisplayGroup[]>([]);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(
    new Set()
  );
  const [showGroupNamePrompt, setShowGroupNamePrompt] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const draftSequence = useRef(0);
  const groupIds = useRef<{ id: string; fields: Set<string> }[]>([]);
  const draftFields = new Set(draftGroups.flatMap(group => group.fields));
  const getFieldLabel = (field: string) =>
    selectableColumns.find(column => column.name === field)?.label ??
    refLabel(field, dataset, related);
  const getColumnLabel = (field: string) =>
    definition.column_labels?.[field] ?? getFieldLabel(field);
  const materialized = effectiveDisplayColumns(
    definition,
    selectedDetailColumns,
    dataset
  );
  const displayColumns = materialized.flatMap(column => {
    const fields = column.fields.filter(field => !draftFields.has(field));
    if (fields.length === 0) return [];
    return fields.length > 1
      ? [{ ...column, fields }]
      : [{ label: getColumnLabel(fields[0]), fields }];
  });
  const previousGroupIds = groupIds.current;
  const nextGroupIds: { id: string; fields: Set<string> }[] = [];
  const displayEntryIds = displayColumns.map(column => {
    if (column.fields.length === 1)
      return `display-entry-field:${column.fields[0]}`;
    const currentFields = new Set(column.fields);
    const candidate = previousGroupIds
      .filter(entry => !nextGroupIds.some(assigned => assigned.id === entry.id))
      .map(entry => ({
        entry,
        overlap: [...entry.fields].filter(field => currentFields.has(field))
          .length,
      }))
      .filter(({ overlap }) => overlap > 0)
      .sort((left, right) => right.overlap - left.overlap)[0]?.entry;
    const fallback = `display-group:${encodeURIComponent([...currentFields].sort().join('|'))}`;
    const id = candidate?.id ?? fallback;
    nextGroupIds.push({ id, fields: currentFields });
    return id;
  });
  useLayoutEffect(() => {
    groupIds.current = nextGroupIds;
  });
  const displaySourceIndexes = displayColumns.map(column =>
    materialized.findIndex(original =>
      original.fields.some(field => column.fields.includes(field))
    )
  );
  const groupTargets = displayColumns.flatMap((column, index) =>
    column.fields.length > 1
      ? [{ sourceIndex: displaySourceIndexes[index], label: column.label }]
      : []
  );
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    setDraftGroups(groups => {
      if (selectedDetailColumns.length === 0) return [];
      return groups.map(group => ({
        ...group,
        fields: group.fields.filter(field =>
          selectedDetailColumns.includes(field)
        ),
      }));
    });
  }, [selectedDetailColumns.join('|')]);

  const moveEntry = (from: number, to: number) => {
    const source = displaySourceIndexes[from] ?? from;
    const target = displaySourceIndexes[to] ?? to;
    onChange(moveDisplayColumn(definition, source, target, dataset));
  };

  const commitDraftField = (draftId: string, field: string) => {
    const draft = draftGroups.find(group => group.id === draftId);
    if (!draft || draft.fields.includes(field)) return;
    const fields = [...draft.fields, field];
    if (fields.length < 2) {
      setDraftGroups(groups =>
        groups.map(group =>
          group.id === draftId ? { ...group, fields } : group
        )
      );
      return;
    }
    const next = addDisplayColumn(definition, draft.label, fields, dataset);
    const columns = next.display_columns ?? [];
    const groupIndex = columns.findIndex(
      column =>
        column.fields.length === fields.length &&
        fields.every(name => column.fields.includes(name)) &&
        column.label === draft.label.trim()
    );
    const appended =
      groupIndex < 0
        ? next
        : {
            ...next,
            display_columns: [
              ...columns.slice(0, groupIndex),
              ...columns.slice(groupIndex + 1),
              columns[groupIndex],
            ],
          };
    onChange(appended);
    setDraftGroups(groups => groups.filter(group => group.id !== draftId));
  };

  const moveDraftField = (groupId: string, from: number, to: number) => {
    if (from === to) return;
    setDraftGroups(groups =>
      groups.map(group => {
        if (group.id !== groupId) return group;
        if (
          from < 0 ||
          to < 0 ||
          from >= group.fields.length ||
          to >= group.fields.length
        )
          return group;
        const fields = [...group.fields];
        const [field] = fields.splice(from, 1);
        fields.splice(to, 0, field);
        return { ...group, fields };
      })
    );
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    const kind = active.data.current?.kind;

    if (kind === 'entry') {
      const from = displayEntryIds.indexOf(activeId);
      const targetField = overId.startsWith('display-entry-field:')
        ? overId.slice('display-entry-field:'.length)
        : overId.startsWith('display-child:')
          ? overId.slice('display-child:'.length)
          : undefined;
      const to = targetField
        ? displayColumns.findIndex(column =>
            column.fields.includes(targetField)
          )
        : displayEntryIds.indexOf(overId);
      if (from >= 0 && to >= 0) moveEntry(from, to);
      return;
    }

    // Membership is edited by the checkboxes and grouped-column selectors.
    // Dragging a child only reorders children inside its existing group.
    if (kind !== 'child') return;
    const fieldPrefix = 'display-child:';
    if (!activeId.startsWith(fieldPrefix) || !overId.startsWith(fieldPrefix))
      return;
    const field = activeId.slice(fieldPrefix.length);
    const targetField = overId.slice(fieldPrefix.length);
    const sourceDraftId =
      typeof active.data.current?.draftId === 'string'
        ? active.data.current.draftId
        : undefined;
    const targetDraftId =
      typeof over.data.current?.draftId === 'string'
        ? over.data.current.draftId
        : undefined;
    const targetPosition = targetDraftId
      ? (draftGroups
          .find(group => group.id === targetDraftId)
          ?.fields.indexOf(targetField) ?? -1)
      : -1;

    if (sourceDraftId) {
      if (!targetDraftId || sourceDraftId !== targetDraftId) return;
      const source = draftGroups.find(group => group.id === sourceDraftId);
      const from = source?.fields.indexOf(field) ?? -1;
      if (from >= 0 && targetPosition >= 0)
        moveDraftField(sourceDraftId, from, targetPosition);
      return;
    }

    const sourceIndex = active.data.current?.sourceIndex;
    const targetIndex = displayColumns.findIndex(column =>
      column.fields.includes(targetField)
    );
    const targetSourceIndex = displaySourceIndexes[targetIndex];
    if (
      typeof sourceIndex !== 'number' ||
      sourceIndex < 0 ||
      sourceIndex !== targetSourceIndex
    )
      return;
    const sourceColumn = materialized[targetSourceIndex];
    const from = sourceColumn?.fields.indexOf(field) ?? -1;
    const to = sourceColumn?.fields.indexOf(targetField) ?? -1;
    if (from >= 0 && to >= 0 && from !== to)
      onChange(
        reorderDisplayColumnFields(
          definition,
          targetSourceIndex,
          from,
          to,
          dataset
        )
      );
  };

  const toggleField = (field: string) =>
    onChange(toggleDetailColumn(definition, field, dataset));
  const removeDraftField = (draftId: string, field: string) => {
    setDraftGroups(groups =>
      groups.map(group =>
        group.id === draftId
          ? { ...group, fields: group.fields.filter(value => value !== field) }
          : group
      )
    );
    toggleField(field);
  };
  const removeChild = (sourceIndex: number, field: string) =>
    onChange(
      moveDisplayColumnFieldToBottom(definition, sourceIndex, field, dataset)
    );
  const moveChild = (sourceIndex: number, from: number, to: number) => {
    const displayIndex = displaySourceIndexes.indexOf(sourceIndex);
    const visible = displayColumns[displayIndex]?.fields;
    const full = materialized[sourceIndex]?.fields;
    const fromField = visible?.[from];
    const toField = visible?.[to];
    if (!full || !fromField || !toField) return;
    const fullFrom = full.indexOf(fromField);
    const fullTo = full.indexOf(toField);
    if (fullFrom < 0 || fullTo < 0) return;
    onChange(
      reorderDisplayColumnFields(
        definition,
        sourceIndex,
        fullFrom,
        fullTo,
        dataset
      )
    );
  };
  const confirmAddDraftGroup = () => {
    const label = newGroupName.trim();
    if (!label) return;
    setDraftGroups(groups => [
      ...groups,
      { id: `draft-group-${++draftSequence.current}`, label, fields: [] },
    ]);
    setNewGroupName('');
    setShowGroupNamePrompt(false);
  };

  return (
    <div className='min-w-0 space-y-2 overflow-x-hidden'>
      <div className='flex items-center justify-between'>
        <SectionLabel>Columns</SectionLabel>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          disabled={selectedDetailColumns.length === 0}
          onClick={() => onChange(clearDetailColumns(definition))}
        >
          Clear all ({selectedDetailColumns.length})
        </Button>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={displayEntryIds}
          strategy={verticalListSortingStrategy}
        >
          <div className='space-y-2'>
            {displayColumns.map((column, index) => (
              <SortableDisplayEntry
                key={displayEntryIds[index]}
                id={displayEntryIds[index]}
                column={column}
                index={index}
                sourceIndex={displaySourceIndexes[index]}
                getFieldLabel={getFieldLabel}
                getColumnLabel={getColumnLabel}
                definition={definition}
                onChange={onChange}
                onToggleField={toggleField}
                onMoveEntry={moveEntry}
                onMoveChild={moveChild}
                onRemoveChild={removeChild}
                onMoveToGroup={(sourceIndex, field, targetSourceIndex) =>
                  onChange(
                    moveDisplayColumnField(
                      definition,
                      sourceIndex,
                      field,
                      targetSourceIndex,
                      undefined,
                      dataset
                    )
                  )
                }
                groupTargets={groupTargets}
                onDelete={() =>
                  onChange(
                    removeDisplayColumn(
                      definition,
                      displaySourceIndexes[index],
                      dataset
                    )
                  )
                }
                collapsed={collapsedGroupIds.has(displayEntryIds[index])}
                onToggleCollapsed={() =>
                  setCollapsedGroupIds(previous => {
                    const next = new Set(previous);
                    const id = displayEntryIds[index];
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  })
                }
                entryCount={displayColumns.length}
              />
            ))}
            {primarySelectableColumns
              .filter(column => !selectedDetailColumns.includes(column.name))
              .map(column => (
                <AvailableDisplayField
                  key={column.name}
                  field={column.name}
                  label={column.label}
                  onToggle={() => toggleField(column.name)}
                />
              ))}
            {!roster &&
              [...related.entries()].map(([key, entry]) => (
                <div key={key} className='space-y-1.5 pt-2'>
                  <SectionLabel>{entry.label}</SectionLabel>
                  {entry.columns
                    .filter(
                      column =>
                        !selectedDetailColumns.includes(`${key}.${column.name}`)
                    )
                    .map(column => {
                      const ref = `${key}.${column.name}`;
                      return (
                        <AvailableDisplayField
                          key={ref}
                          field={ref}
                          label={column.label}
                          onToggle={() => toggleField(ref)}
                        />
                      );
                    })}
                </div>
              ))}
            {draftGroups.map(group => (
              <DraftGroup
                key={group.id}
                group={group}
                collapsed={collapsedGroupIds.has(`draft:${group.id}`)}
                selectedDetailColumns={selectedDetailColumns.filter(
                  field =>
                    !draftGroups.some(
                      other =>
                        other.id !== group.id && other.fields.includes(field)
                    )
                )}
                getFieldLabel={getFieldLabel}
                definition={definition}
                onChange={onChange}
                onToggleCollapsed={() =>
                  setCollapsedGroupIds(previous => {
                    const next = new Set(previous);
                    const id = `draft:${group.id}`;
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  })
                }
                onDelete={() =>
                  setDraftGroups(groups =>
                    groups.filter(item => item.id !== group.id)
                  )
                }
                onLabelChange={label =>
                  setDraftGroups(groups =>
                    groups.map(item =>
                      item.id === group.id ? { ...item, label } : item
                    )
                  )
                }
                onAddField={field => commitDraftField(group.id, field)}
                onRemoveField={field => removeDraftField(group.id, field)}
                onMoveField={(from, to) => moveDraftField(group.id, from, to)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {showGroupNamePrompt && (
        <div
          role='group'
          aria-label='Name new grouped column'
          className='space-y-2 rounded border p-2'
        >
          <Label htmlFor='new-group-name'>New grouped column name</Label>
          <Input
            id='new-group-name'
            inputSize='sm'
            aria-label='New grouped column name'
            value={newGroupName}
            onChange={event => setNewGroupName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') confirmAddDraftGroup();
            }}
          />
          <div className='flex gap-1'>
            <Button
              type='button'
              size='sm'
              variant='outline'
              disabled={!newGroupName.trim()}
              onClick={confirmAddDraftGroup}
            >
              <Plus className='h-4 w-4' /> Create grouped column
            </Button>
            <Button
              type='button'
              size='sm'
              variant='ghost'
              onClick={() => {
                setNewGroupName('');
                setShowGroupNamePrompt(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      <Button
        type='button'
        size='sm'
        variant='outline'
        onClick={() => {
          setNewGroupName('');
          setShowGroupNamePrompt(true);
        }}
      >
        <Plus className='h-4 w-4' /> Add a new grouped column
      </Button>
    </div>
  );
};

const AggregateColumnLabels = ({
  definition,
  columns,
  onChange,
}: {
  definition: ReportDefinition;
  columns: { key: string; defaultName: string }[];
  onChange: (definition: ReportDefinition) => void;
}) => (
  <div className='space-y-2'>
    <p className='text-xs text-muted-foreground'>Rename a result column.</p>
    {columns.map(column => (
      <div key={column.key} className='flex items-center gap-1'>
        <LabelInput
          definition={definition}
          field={column.key}
          defaultName={column.defaultName}
          onChange={onChange}
        />
      </div>
    ))}
  </div>
);

const TableColumnsEditor = ({
  definition,
  dataset,
  related = NO_RELATED,
  onChange,
}: TableColumnsEditorProps) => {
  if (!dataset) return null;
  const aggregate =
    definition.pipeline?.type !== 'patient_roster' &&
    (definition.group_bys.length > 0 || definition.measures.length > 0);
  if (!aggregate) {
    return (
      <DetailColumnsEditor
        dataset={dataset}
        definition={definition}
        related={related}
        onChange={onChange}
      />
    );
  }
  const keys = resultColumnKeys(definition, dataset);
  const defaults = defaultColumnLabels(
    dataset,
    related,
    columnLabelRefs(definition, dataset)
  );
  const aggregateColumns = keys.map(key => ({
    key,
    defaultName: humanizeHeader(
      key,
      defaults,
      new Map(dataset.columns.map(column => [column.name, column]))
    ),
  }));
  return (
    <AggregateColumnLabels
      definition={definition}
      columns={aggregateColumns}
      onChange={onChange}
    />
  );
};

export default TableColumnsEditor;
